import type {
  AccessGrant,
  AccessResourceType,
  BulkAccessPreview,
  BulkAccessPreviewRequest,
  BulkAccessRequest,
  BulkAccessResult,
  BulkAccessTarget,
} from '~/features/access-control/types/AccessControl';

import {
  clauseResource,
  clauseVariantResource,
  departmentSearchFixtures,
  documentResource,
  folderResource,
  prototypeOwnerGrant,
  userSearchFixtures,
} from '~/features/access-control/fixtures/manageAccess';
import {
  countTargetsByType,
  filterTargetsByScope,
} from '~/features/access-control/utils/bulkAccess';
import { getPrincipalKey } from '~/features/access-control/utils/principal';

export type MockResourceAccess = {
  grants: AccessGrant[];
  inheritFromParent: boolean;
};

type ResourceKey = `${AccessResourceType}:${string}`;

const toKey = (
  resourceType: AccessResourceType,
  resourceId: string,
): ResourceKey => `${resourceType}:${resourceId}`;

const engineeringGrant: AccessGrant = {
  principal: departmentSearchFixtures[0]!.principal,
  role: 'view',
  source: 'direct',
};

const carmenGrant: AccessGrant = {
  principal: userSearchFixtures[0]!.principal,
  role: 'edit',
  source: 'direct',
};

function inheritedFrom(grant: AccessGrant, sourceName: string): AccessGrant {
  return { ...grant, source: 'inherited', sourceName };
}

/** Seed data: a document with no overrides inherits everything from its
 * folder, and a clause variant mixes inherited and direct grants. */
function createInitialStore(): Map<ResourceKey, MockResourceAccess> {
  const store = new Map<ResourceKey, MockResourceAccess>();

  store.set(toKey(folderResource.type, folderResource.id), {
    grants: [prototypeOwnerGrant, engineeringGrant],
    inheritFromParent: false,
  });

  store.set(toKey(documentResource.type, documentResource.id), {
    grants: [
      inheritedFrom(prototypeOwnerGrant, folderResource.name),
      inheritedFrom(engineeringGrant, folderResource.name),
    ],
    inheritFromParent: true,
  });

  store.set(toKey(clauseResource.type, clauseResource.id), {
    grants: [prototypeOwnerGrant, carmenGrant],
    inheritFromParent: false,
  });

  store.set(toKey(clauseVariantResource.type, clauseVariantResource.id), {
    grants: [
      inheritedFrom(prototypeOwnerGrant, clauseResource.name),
      carmenGrant,
    ],
    inheritFromParent: true,
  });

  return store;
}

let store = createInitialStore();

const mockFolderBulkTargets = new Map<string, BulkAccessTarget[]>();

function createSyntheticFolderTargets(folderId: string): BulkAccessTarget[] {
  return [
    { id: folderId, name: 'Selected folder', type: 'folder' },
    {
      id: `${folderId}-document-1`,
      name: 'Document 1.pdf',
      type: 'document',
    },
    {
      id: `${folderId}-document-2`,
      name: 'Document 2.pdf',
      type: 'document',
    },
  ];
}

function seedMockFolderBulkTargets(): void {
  mockFolderBulkTargets.set(folderResource.id, [
    { id: folderResource.id, name: folderResource.name, type: 'folder' },
    {
      id: documentResource.id,
      name: documentResource.name,
      type: 'document',
    },
  ]);
  mockFolderBulkTargets.set('folder-42', [
    { id: 'folder-42', name: 'Contracts', type: 'folder' },
    { id: 'folder-42-sub-1', name: 'Commercial', type: 'folder' },
    { id: 'folder-42-sub-2', name: 'Vendor', type: 'folder' },
    { id: 'folder-42-sub-3', name: 'Legal', type: 'folder' },
    ...Array.from({ length: 12 }, (_, index) => ({
      id: `folder-42-doc-${index + 1}`,
      name: `Document ${index + 1}.pdf`,
      type: 'document' as const,
    })),
  ]);
}

/** Test-only: puts the store back to its seeded state between specs. */
export function resetAccessControlMockStore(): void {
  store = createInitialStore();
  bulkTargetMetadata.clear();
  mockFolderBulkTargets.clear();
  seedBulkTargetMetadata();
  seedMockFolderBulkTargets();
}

export function getMockResourceAccess(
  resourceType: AccessResourceType,
  resourceId: string,
): MockResourceAccess {
  const key = toKey(resourceType, resourceId);
  const existing = store.get(key);
  if (existing) {
    return existing;
  }

  let defaultAccess: MockResourceAccess;
  if (resourceType === 'clause') {
    defaultAccess = {
      grants: [prototypeOwnerGrant, carmenGrant],
      inheritFromParent: false,
    };
  } else if (resourceType === 'clause_variant') {
    defaultAccess = {
      grants: [
        inheritedFrom(prototypeOwnerGrant, 'Parent clause'),
        carmenGrant,
      ],
      inheritFromParent: true,
    };
  } else {
    defaultAccess = {
      grants: [prototypeOwnerGrant],
      inheritFromParent: true,
    };
  }
  store.set(key, defaultAccess);
  return defaultAccess;
}

/** Server-shaped diff: identifies removed grants by principal key rather than
 * a full principal object, since that's all the store needs to remove them. */
export type MockAccessDiff = {
  inheritFromParent: boolean;
  addedGrants: AccessGrant[];
  updatedGrants: AccessGrant[];
  removedPrincipalKeys: string[];
};

export function updateMockResourceAccess(
  resourceType: AccessResourceType,
  resourceId: string,
  diff: MockAccessDiff,
): MockResourceAccess {
  const current = getMockResourceAccess(resourceType, resourceId);

  const removedKeys = new Set(diff.removedPrincipalKeys);
  const updatedByKey = new Map(
    diff.updatedGrants.map((grant) => [
      getPrincipalKey(grant.principal),
      grant,
    ]),
  );

  const survivingGrants = current.grants
    .filter((grant) => !removedKeys.has(getPrincipalKey(grant.principal)))
    .map(
      (grant) => updatedByKey.get(getPrincipalKey(grant.principal)) ?? grant,
    );

  const next: MockResourceAccess = {
    grants: [...survivingGrants, ...diff.addedGrants],
    inheritFromParent: diff.inheritFromParent,
  };

  store.set(toKey(resourceType, resourceId), next);
  return next;
}

type BulkTargetMetadata = {
  hasUniqueGrants: boolean;
  copyCount: number;
};

const bulkTargetMetadata = new Map<string, BulkTargetMetadata>();

function targetKey(target: BulkAccessTarget): string {
  return `${target.type}:${target.id}`;
}

function seedBulkTargetMetadata(): void {
  bulkTargetMetadata.set(`${folderResource.type}:${folderResource.id}`, {
    hasUniqueGrants: false,
    copyCount: 0,
  });
  bulkTargetMetadata.set(`${documentResource.type}:${documentResource.id}`, {
    hasUniqueGrants: true,
    copyCount: 2,
  });
}

seedBulkTargetMetadata();
seedMockFolderBulkTargets();

/**
 * Resolves a bulk selection for the mock store. Select-all is normally
 * expanded server-side from `folderId`; this mirrors that for the
 * deployed-environment fallback.
 */
export function resolveMockBulkTargets({
  folderId,
  selectAll,
  targets,
}: BulkAccessPreviewRequest): BulkAccessTarget[] {
  if (!selectAll) {
    return targets;
  }

  if (!folderId) {
    throw new Error(
      'Bulk access select-all requires a folderId so the server can resolve the selection.',
    );
  }

  return (
    mockFolderBulkTargets.get(folderId) ??
    createSyntheticFolderTargets(folderId)
  );
}

export function previewDocumentBulkAccessForRequest(
  request: BulkAccessPreviewRequest,
): BulkAccessPreview {
  return previewDocumentBulkAccess(resolveMockBulkTargets(request));
}

export function applyDocumentBulkAccessForRequest(
  request: BulkAccessRequest,
): BulkAccessResult {
  return applyDocumentBulkAccess({
    ...request,
    targets: resolveMockBulkTargets(request),
  });
}

function getBulkTargetMetadata(target: BulkAccessTarget): BulkTargetMetadata {
  return (
    bulkTargetMetadata.get(targetKey(target)) ?? {
      hasUniqueGrants: false,
      copyCount: 0,
    }
  );
}

function hasDirectGrants(access: MockResourceAccess): boolean {
  return access.grants.some(
    (grant) => grant.source === 'direct' && !grant.isOwner,
  );
}

function buildAccessSummary(grants: AccessGrant[]): string {
  const names = grants
    .filter((grant) => !grant.isOwner)
    .map((grant) => grant.principal.name);
  return names.slice(0, 3).join(', ') + (names.length > 3 ? ',...' : '');
}

export function previewDocumentBulkAccess(
  targets: BulkAccessTarget[],
): BulkAccessPreview {
  const { documentCount, folderCount } = countTargetsByType(targets);

  let mixedStateCount = 0;
  let copiesCount = 0;

  for (const target of targets) {
    const metadata = getBulkTargetMetadata(target);
    const access = getMockResourceAccess(target.type, target.id);
    if (metadata.hasUniqueGrants || hasDirectGrants(access)) {
      mixedStateCount += 1;
    }
    copiesCount += metadata.copyCount;
  }

  return {
    folderCount,
    documentCount,
    mixedStateCount,
    copiesCount,
  };
}

export function applyDocumentBulkAccess(
  request: BulkAccessRequest,
): BulkAccessResult {
  // The endpoint owns scoping the full selection down to what the admin
  // actually chose to apply to — the caller sends the whole selection plus
  // the chosen scope, not a pre-filtered target list, so the server (and
  // this mock) is the single source of truth for what "scope" meant.
  const scopedTargets = filterTargetsByScope(request.targets, request.scope);
  let copiesAppliedCount = 0;

  const items = scopedTargets.map((target) => {
    const metadata = getBulkTargetMetadata(target);
    if (request.copiesChoice === 'apply' && target.type === 'document') {
      copiesAppliedCount += metadata.copyCount;
    }
    const current = getMockResourceAccess(target.type, target.id);
    const nonOwnerGrants = current.grants.filter((grant) => !grant.isOwner);
    const priorGrantsRemoved =
      request.mode === 'override' ? nonOwnerGrants.length : 0;

    if (request.mode === 'override') {
      updateMockResourceAccess(target.type, target.id, {
        inheritFromParent: current.inheritFromParent,
        addedGrants: request.grants.map((grant) => ({
          ...grant,
          source: 'direct',
        })),
        updatedGrants: [],
        removedPrincipalKeys: nonOwnerGrants.map((grant) =>
          getPrincipalKey(grant.principal),
        ),
      });
    } else {
      const existingKeys = new Set(
        current.grants.map((grant) => getPrincipalKey(grant.principal)),
      );
      const grantsToAdd = request.grants
        .filter((grant) => !existingKeys.has(getPrincipalKey(grant.principal)))
        .map((grant) => ({ ...grant, source: 'direct' as const }));

      if (grantsToAdd.length > 0) {
        updateMockResourceAccess(target.type, target.id, {
          inheritFromParent: current.inheritFromParent,
          addedGrants: grantsToAdd,
          updatedGrants: [],
          removedPrincipalKeys: [],
        });
      }
    }

    const updated = getMockResourceAccess(target.type, target.id);

    return {
      id: target.id,
      type: target.type,
      name: target.name,
      priorGrantsRemoved:
        priorGrantsRemoved > 0 ? priorGrantsRemoved : undefined,
      accessSummary: buildAccessSummary(updated.grants),
    };
  });

  return {
    mode: request.mode,
    scope: request.scope,
    updatedCount: scopedTargets.length + copiesAppliedCount,
    items,
    grantsApplied: request.grants,
    locationLabel: 'Documents',
  };
}
